import { atomWithQuery } from "jotai-tanstack-query";
import { keepPreviousData } from "@tanstack/react-query";
import { atom } from "jotai";
import { atomFamily } from "jotai-family";
import { authAtom } from "../../auth/atoms";
import { SearchApiParams } from "../../../types/search";
import { loadLocations } from "../../../apis/search";
import { businessInterestsAtom } from "../interests/atoms";
import { BusinessInterestType } from "../../../types/tagging";
import { homePinsQueryKey } from "./homePinsQueryKey";

// Finite, non-zero coords only — avoids firing a (0,0) "whole world" search.
const hasValidCoords = (
  lat?: string | null,
  lng?: string | null,
): boolean => {
  const latNum = parseFloat(lat ?? "");
  const lngNum = parseFloat(lng ?? "");
  return (
    Number.isFinite(latNum) &&
    latNum !== 0 &&
    Number.isFinite(lngNum) &&
    lngNum !== 0
  );
};

// ============================================================================
// Search Page Atoms (used by search page, React Native, and other components)
// ============================================================================

export const venueSearchParamsAtom = atom<SearchApiParams>({
  search: "",
  latitude: null,
  longitude: null,
  userlatitude: null,
  userlongitude: null,
  types: "",
  accessibilities: "",
  showVenueOptions: "All",
  addinfo: "",
  placesToReplaceString: "",
  simpleLocationText: "",
  searchThisAreaClicked: "no",
  zoom: "16",
  per_page: "200",
  new_map: "1",
  tagging_mode: "off",
  progressive_update: "yes",
  userToken: "",
  saved_place_id: "",
  saved_place_user_id: "",
  saved_place_sortby: "",
  unixTimestamp: null,
  includeGoogleFallback: "false",
  section_filter: undefined,
  mapper_category: undefined,
});


export const homePinsAtom = atomWithQuery((get) => {
  const params = get(venueSearchParamsAtom);
  const auth = get(authAtom);
  const accessToken = auth?.data?.accessToken;

  const isEnabled =
    hasValidCoords(params.latitude, params.longitude) && !auth.isLoading;

  return {
    queryKey: homePinsQueryKey(params, accessToken),
    queryFn: async ({ queryKey: [, params, accessToken] }) => {
      const parameters = params as SearchApiParams;
      //const user = get(authAtom)?.data;
      if (!hasValidCoords(parameters.latitude, parameters.longitude)) {

        return {
          locations: [],
          accessibility_tags: [],
          newRegion: null,
          newLocation: null,
          search_original_distance: null,
          search_distance_used: null,
          filtered_venues: [],
        };
      }

      let response;
      try {
        response = await loadLocations(
          accessToken as unknown as string | null | undefined,
          parameters
        );
      } catch (error) {
        console.error("Error loading pins", error);
        return {
          locations: [],
          accessibility_tags: [],
          newRegion: null,
          newLocation: null,
          total_count: 0,
          filtered_venues: [],
        };
      }

      const selectedVenueTypes = parameters.types.split("|");
      if (selectedVenueTypes.length <= 0) {
        return response;
      }

      // Get business interests from shared atom instead of making a direct API call
      const businessInterestsData = get(businessInterestsAtom);
      const businessInterests: BusinessInterestType[] = businessInterestsData?.data || [];

      const locations = response.locations.map((location) => {
        const businessInterest = businessInterests.find((businessInterest) => businessInterest.business_id === location.business.id);
        const types = location.types.split("|");
        for (const type of selectedVenueTypes) {
          if (types.includes(type.toLowerCase())) {
            return {
              ...location,
              calcVenueType: type,
              businessInterest: businessInterest
            };
          }
        }
        return {
          ...location,
          businessInterest: businessInterest
        };
      });

      return {
        locations: locations,
        accessibility_tags: response.accessibility_tags,
        newRegion: response.newRegion,
        newLocation: response.newLocation,
        search_original_distance: response.search_original_distance,
        search_distance_used: response.search_distance_used,
        total_count: response.total_count,
        filtered_venues: response.filtered_venues,
      };
    },
    enabled: isEnabled,
    // Keep pins visible during reloads (no blank / full-page loader).
    placeholderData: keepPreviousData,
  }
});

export const accessibilityTagsAtom = atom<any[]>((get) => {
  const data = get(homePinsAtom);
  return data?.data?.accessibility_tags || [];
});

// Desktop home page accessibility tags atom
export const desktopHomePageAccessibilityTagsAtom = atom<any[]>((get) => {
  const viewMode = get(desktopHomePageViewModeAtom);
  
  // In mapper mode, read from the mapper query (undefined mappingStatus)
  // In standard mode, read from the mapped venues query
  const data = viewMode === "mapper"
    ? get(desktopHomePagePinsAtomFamily(undefined))
    : get(desktopHomePagePinsAtomFamily("mapped"));
    
  return data?.data?.accessibility_tags || [];
});

export const homeUnTaggedPinsAtom = atomWithQuery((get) => {
  const params = get(venueSearchParamsAtom);
  const auth = get(authAtom);
  const accessToken = auth?.data?.accessToken;

  const isEnabled =
    hasValidCoords(params.latitude, params.longitude) && !auth.isLoading;

  return {
    queryKey: [
      "homeUntaggedPins",
      params,
      accessToken,
    ],
    queryFn: async ({ queryKey: [, params, accessToken] }) => {
      const parameters = { ...params as SearchApiParams, tagging_mode: "on" };
      //const user = get(authAtom)?.data;
      if (!hasValidCoords(parameters.latitude, parameters.longitude)) {
        
        return {
          locations: [],
          accessibility_tags: [],
          newRegion: null,
          newLocation: null,
          search_original_distance: null,
          search_distance_used: null
        };
      }
     
      let response;
      try {
        response = await loadLocations(
          accessToken as unknown as string | null | undefined,
          parameters
        );
      } catch (error) {
        console.error("Error loading untagged pins", error);
        return {
          locations: [],
          accessibility_tags: [],
          newRegion: null,
          newLocation: null,
          total_count: 0,
        };
      }

      const selectedVenueTypes = parameters.types.split("|");
      if (selectedVenueTypes.length <= 0) {
        return response;
      }

      // Get business interests from shared atom instead of making a direct API call
      const businessInterestsData = get(businessInterestsAtom);
      const businessInterests: BusinessInterestType[] = businessInterestsData?.data || [];

      const locations = response.locations.map((location) => {
        const businessInterest = businessInterests.find((businessInterest) => businessInterest.business_id === location.business.id);
        const types = location.types.split("|");
        for (const type of selectedVenueTypes) {
          if (types.includes(type.toLowerCase())) {
            return {
              ...location,
              calcVenueType: type,
              businessInterest: businessInterest
            };
          }
        }
        return {
          ...location,
          businessInterest: businessInterest
        };
      });

      return {
        locations: locations,
        accessibility_tags: response.accessibility_tags,
        newRegion: response.newRegion,
        newLocation: response.newLocation,
        search_original_distance: response.search_original_distance,
        search_distance_used: response.search_distance_used,
        total_count: response.total_count,
        filtered_venues: response.filtered_venues,
      };
    },
    enabled: isEnabled,
  }
});

// ============================================================================
// Desktop Home Page Atoms (used only by index.lazy.tsx and DesktopHomePage)
// ============================================================================

export const desktopHomePageSearchParamsAtom = atom<SearchApiParams>({
  search: "",
  latitude: null,
  longitude: null,
  userlatitude: null,
  userlongitude: null,
  types: "",
  accessibilities: "",
  showVenueOptions: "All",
  addinfo: "",
  placesToReplaceString: "",
  simpleLocationText: "",
  searchThisAreaClicked: "no",
  zoom: "16",
  new_map: "1",
  tagging_mode: "off",
  progressive_update: "yes",
  userToken: "",
  saved_place_id: "",
  saved_place_user_id: "",
  saved_place_sortby: "",
  unixTimestamp: null,
  includeGoogleFallback: "false",
  section_filter: undefined,
  mapper_category: undefined,
});


// ============================================================================
// Desktop Home Page Pins Atom Family
// ============================================================================

// Desktop home page view mode (mapper vs standard)
// Initialize to null to prevent queries from executing before view mode is determined  
const initialViewMode: "mapper" | "standard" | null = null;
export const desktopHomePageViewModeAtom = atom(initialViewMode);

// Parameterized atom family for desktop home page pins with optional mapping_status filter
// Uses string keys (mappingStatus only) for referential stability
export const desktopHomePagePinsAtomFamily = atomFamily((mappingStatus?: "mapped" | "unmapped") =>
  atomWithQuery((get) => {
    const searchParams = get(desktopHomePageSearchParamsAtom);
    const auth = get(authAtom);
    const accessToken = auth?.data?.accessToken;

    // Read view mode to determine if this query should be enabled
    const viewMode = get(desktopHomePageViewModeAtom);
    
    // Don't enable any queries until view mode is explicitly set (not null)
    // This prevents queries from executing with the wrong view mode during initialization
    const enabledFromViewMode = viewMode !== null && (
      mappingStatus === undefined
        ? viewMode === "mapper"  // Mapper query enabled only in mapper mode
        : viewMode === "standard"  // Mapped/unmapped queries enabled only in standard mode
    );

    const isEnabled =
      hasValidCoords(searchParams.latitude, searchParams.longitude) &&
      !auth.isLoading &&
      enabledFromViewMode;

    // Generate query key based on mapping status
    const queryKeyPrefix = mappingStatus
      ? `desktopHome${mappingStatus.charAt(0).toUpperCase() + mappingStatus.slice(1)}Pins`
      : "desktopHomePins";

    return {
      queryKey: [
        queryKeyPrefix,
        searchParams,
        accessToken,
        mappingStatus,
      ],
      queryFn: async ({ queryKey: [, searchParams, accessToken] }) => {
        // Apply mapping_status filter if provided
        // For unmapped venues, also set tagging_mode to "on"
        // For mapper view (undefined mappingStatus), set tagging_mode to "on" to get all venues
        const parameters = mappingStatus
          ? {
            ...searchParams as SearchApiParams,
            mapping_status: mappingStatus,
            zoom: "16", // Desktop home page searches at street zoom (matches search map)
            per_page: "40", // Home shows 10 in list; 40 keeps preview map dense enough
            ...(mappingStatus === "unmapped" && { tagging_mode: "on" })
          }
          : { 
            ...searchParams as SearchApiParams,
            tagging_mode: "on", // For mapper view, get all venues
            zoom: "16", // Desktop home page searches at street zoom (matches search map)
            per_page: "40", // Home shows 10 per category; 40 keeps categories populated
          };

        if (!hasValidCoords(parameters.latitude, parameters.longitude)) {
          return {
            locations: [],
            accessibility_tags: [],
            newRegion: null,
            newLocation: null,
            search_original_distance: null,
            search_distance_used: null
          };
        }

        let response;
        try {
          response = await loadLocations(
            accessToken as unknown as string | null | undefined,
            parameters
          );
        } catch (error) {
          console.error(`Error loading ${mappingStatus || ''} pins (desktop home)`, error);
          return {
            locations: [],
            accessibility_tags: [],
            newRegion: null,
            newLocation: null,
          };
        }

        const selectedVenueTypes = parameters.types.split("|");
        if (selectedVenueTypes.length <= 0) {
          return response;
        }

        // Get business interests from shared atom instead of making a direct API call
        const businessInterestsData = get(businessInterestsAtom);
        const businessInterests: BusinessInterestType[] = businessInterestsData?.data || [];

        const locations = response.locations.map((location) => {
          const businessInterest = businessInterests.find((businessInterest) => businessInterest.business_id === location.business.id);
          const types = location.types.split("|");
          for (const type of selectedVenueTypes) {
            if (types.includes(type.toLowerCase())) {
              return {
                ...location,
                calcVenueType: type,
                businessInterest: businessInterest
              };
            }
          }
          return {
            ...location,
            businessInterest: businessInterest
          };
        });

        return {
          locations: locations,
          accessibility_tags: response.accessibility_tags,
          newRegion: response.newRegion,
          newLocation: response.newLocation,
          search_original_distance: response.search_original_distance,
          search_distance_used: response.search_distance_used,
        };
      },
      enabled: isEnabled,
    }
  })
);

// Export convenience atoms for desktop home page
export const desktopHomePageTaggedPinsAtom = desktopHomePagePinsAtomFamily("mapped");
export const desktopHomePageUnTaggedPinsAtom = desktopHomePagePinsAtomFamily("unmapped");

